import {
  CallIcon,
  DownArrowIcon,
  IncomingCallIcon,
  OutgoingCallIcon,
  RightArrowIcon,
  UpArrowIcon,
} from '@/Components/Common/Icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import { ScheduleCallCellIcon } from '@/Components/Dashboard/icons';
import { UserImage } from '@/Components/Schedule/UserImage';
import { EVENT_STATUS, SCHEDULE_EVENT_TYPE } from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getProfilePics } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import {
  getProfilePicUrl,
  getProfilePicUrlFromArray,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
  PhotoResolution,
  VideoCallStatusUpdate,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { Card, Tooltip } from 'antd';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import '../RecentActivity.less';
import {
  formatTime,
  getCallTitle,
  getDateLabel,
  getStatusInfo,
  isCallInProgress,
  LoadingCard,
} from '../RecentActivityUtils';

const renderCallCell = ({ notification, useruuid, resourcesMap, trans }) => {
  const invitedContacts = notification?.invited_contacts || [];
  const isAdhoc = notification?.incoming !== undefined && invitedContacts.length <= 2;
  const isSchedule = notification?.event_type === SCHEDULE_EVENT_TYPE.SCHEDULE;
  const scheduleCallTitle = getCallTitle(notification, trans);
  const otherContacts = useruuid
    ? invitedContacts.filter((c) => c?.useruuid !== useruuid)
    : invitedContacts;
  const displayContacts = isAdhoc ? otherContacts.slice(0, 1) : otherContacts.slice(0, 2);

  return (
    <div className="call-cell-content">
      {isAdhoc && (
        <span className="call-direction-icon">
          {notification.incoming ? <IncomingCallIcon /> : <OutgoingCallIcon />}
        </span>
      )}
      {isSchedule ? (
        <>
          <span className="call-direction-icon">
            <ScheduleCallCellIcon />
          </span>
          <Tooltip title={scheduleCallTitle} placement="bottom">
            <span className="schedule-cell-title">{scheduleCallTitle}</span>
          </Tooltip>
        </>
      ) : (
        displayContacts.length > 0 && (
          <div className="call-cell-avatars">
            {displayContacts.map((contact, index) => {
              const url =
                getProfilePicUrl(contact.useruuid, resourcesMap, PhotoResolution.R64) ||
                getProfilePicUrlFromArray(contact.resources, PhotoResolution.R64);
              const name = `${contact.firstname || ''} ${contact.lastname || ''}`.trim();
              const key = contact.useruuid || contact.id || `contact-${index}`;
              return (
                <Tooltip key={key} title={name} placement="bottom">
                  <span>
                    <UserImage url={url} />
                  </span>
                </Tooltip>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

const RecentCallsTable = ({ timezone, is24HourFormat, locale, data, useruuid }) => {
  const trans = useSelector(translator);
  const resourcesMap = useSelector(getProfilePics());

  const rows = data || [];

  if (rows.length === 0) {
    return <div className="recent-calls-empty">{trans('NODATAAVAIALBLE')}</div>;
  }

  return (
    <div className="recent-calls-table-card">
      <div className="recent-calls-table-head">
        <div className="head-cell">{trans('DATE_L')}</div>
        <div className="head-cell head-cell-indented">{trans('CALL_DESCRIPTION')}</div>
        <div className="head-cell">{trans('STATUS')}</div>
      </div>

      <div className="recent-calls-table-body">
        {rows.map((notification, index) => {
          // For schedule events use event_status; fall back to call notification status
          const eventStatus = notification?.event_status;
          const callStatus = notification?.call_status__status || notification?.call_status;
          const resolvedStatus =
            eventStatus === EVENT_STATUS.COMPLETED
              ? VideoCallStatusUpdate.completed
              : eventStatus === EVENT_STATUS.EXPIRED
                ? VideoCallStatusUpdate.missed
                : eventStatus === EVENT_STATUS.ONGOING && isCallInProgress(notification)
                  ? EVENT_STATUS.ONGOING
                  : callStatus;
          const statusInfo = getStatusInfo(resolvedStatus, trans);

          return (
            <div className="recent-calls-table-row" key={`recent-calls-table-row-${index}`}>
              <div className="row-cell date-cell">
                <span className="date-text">
                  {getDateLabel(notification?.utc_timestamp_inms, trans, timezone, locale)}
                </span>
                <span className="time-text">
                  {formatTime(notification?.utc_timestamp_inms, is24HourFormat, timezone)}
                </span>
              </div>

              <div className="row-cell call-cell">
                {renderCallCell({ notification, useruuid, resourcesMap, trans })}
              </div>

              <div className="row-cell status-cell">
                <span className={`status-pill ${statusInfo.className}`}>
                  {statusInfo.icon}
                  <span>{statusInfo.text}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const RecentCallCard = (props) => {
  const { timezone, is24HourFormat, locale, data, isLoading, trendData, useruuid } = props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();

  if (isLoading) return <LoadingCard />;

  const modalConfig = {
    title: (
      <div className="recent-activity-modal-title">
        <CallIcon className="recent-activity-modal-title-icon" />
        <span>{trans('RECENT_CALLS')}</span>
      </div>
    ),
    content: <RecentCallsTable {...props} />,
  };

  // Get the latest notification (most recent timestamp)
  const sorted = data || [];
  const latestNotification = sorted.length > 0 ? sorted[0] : null;

  const eventStatus = latestNotification?.event_status;
  const callStatus = latestNotification?.call_status__status || latestNotification?.call_status;
  const resolvedStatus =
    eventStatus === EVENT_STATUS.COMPLETED
      ? VideoCallStatusUpdate.completed
      : eventStatus === EVENT_STATUS.EXPIRED
        ? VideoCallStatusUpdate.missed
        : eventStatus === EVENT_STATUS.ONGOING && isCallInProgress(latestNotification)
          ? EVENT_STATUS.ONGOING
          : callStatus;
  const statusInfo = getStatusInfo(resolvedStatus, trans);
  const dateLabel = getDateLabel(latestNotification?.utc_timestamp_inms, trans, timezone, locale);
  const formattedTime = formatTime(
    latestNotification?.utc_timestamp_inms,
    is24HourFormat,
    timezone
  );

  const trendPercent = get(
    trendData,
    ['trendResults', 'completed_calls', 'completed_calls', 'trendPercent'],
    null
  );
  const isUp = trendPercent > 0;
  const isDown = trendPercent < 0;
  const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const handleCardClick = (e) => {
    e.preventDefault();
    openDashboardModal(modalConfig);
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDashboardModal(modalConfig);
    }
  };

  const CardTitle = (
    <div className="recent-activity-card-header">
      <CallIcon className="recent-activity-card-icon" />
      <h4 className="recent-activity-card-title">{trans('CALL_DESCRIPTION')}</h4>
    </div>
  );

  const CallStatus = (
    <span className={`recent-activity-status ${statusInfo.className}`}>{statusInfo.text}</span>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      variant="borderless"
      hoverable
      title={CardTitle}
      extra={CallStatus}
    >
      <div className="recent-activity-card-body">
        <p className="recent-activity-time">
          <span className="recent-activity-date-text">{dateLabel}</span>
          {formattedTime && (
            <span className="recent-activity-time-text">&nbsp;{formattedTime}</span>
          )}
        </p>
        <div className="recent-activity-context">
          {latestNotification?.event_type === SCHEDULE_EVENT_TYPE.SCHEDULE ? (
            <ScheduleCallCellIcon />
          ) : (
            <>
              {latestNotification?.incoming === true && (
                <IncomingCallIcon className="recent-activity-direction-icon" />
              )}
              {latestNotification?.incoming === false && (
                <OutgoingCallIcon className="recent-activity-direction-icon" />
              )}
            </>
          )}
          <span>{getCallTitle(latestNotification, trans, useruuid)}</span>
        </div>
      </div>
      <>
        <hr className="recent-activity-divider" />
        <div className="recent-activity-footer-section">
          <p className="recent-activity-footer-label">{trans('COMPLETED_CALLS')}</p>
          <div className="recent-activity-card-footer">
            <span className="recent-activity-comparison">
              {trans('YESTERDAY_VS_LAST_WEEK_AVG')}
            </span>
            {displayPercent !== null ? (
              <span
                className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}
              >
                {isUp ? (
                  <UpArrowIcon className="trend-arrow" />
                ) : isDown ? (
                  <DownArrowIcon className="trend-arrow" />
                ) : (
                  <RightArrowIcon className="trend-arrow" />
                )}
                <span>{displayPercent}%</span>
              </span>
            ) : (
              <span className="recent-activity-trend neutral">-</span>
            )}
          </div>
        </div>
      </>
    </Card>
  );
};

RecentCallsTable.propTypes = {
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  useruuid: PropTypes.string,
};

RecentCallCard.propTypes = {
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
  useruuid: PropTypes.string,
};
